import React from 'react';
import { Button } from './ui/button';
import { Phone, Youtube, Sparkles } from 'lucide-react';
import { heroData, contactInfo } from '../mock';

export const Hero = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 bg-gradient-to-br from-cream via-white to-sage-green/10">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 border-2 border-sage-green rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 border-2 border-forest-green rounded-full"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 border-2 border-earth rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="order-2 lg:order-1">
            {/* Traditional Badge */}
            <div className="inline-flex items-center px-5 py-2 rounded-full bg-sage-green/10 border border-sage-green/30 mb-6">
              <div className="w-2 h-2 bg-sage-green rounded-full mr-3 animate-pulse"></div>
              <span className="text-forest-green text-sm font-semibold">Ancient Wisdom • Modern Healing</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-forest-green mb-6 leading-tight">
              Authentic Healing<br />
              <span className="text-sage-green">for the</span><br />
              Modern Soul
            </h1>

            {/* Subheadline with decorative element */}
            <div className="relative pl-6 border-l-4 border-sage-green mb-8">
              <p className="text-lg md:text-xl text-earth leading-relaxed">
                Expert Ayurvedic care by Dr. Kirti & Dr. Sameer at our Panchakarma Centre, 
                dedicated to restoring your natural balance through time-tested wisdom.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a href={`tel:${contactInfo.phone}`}>
                <Button 
                  size="lg" 
                  className="bg-forest-green hover:bg-sage-green text-white font-semibold px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105 shadow-lg w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Book a Consultation
                </Button>
              </a>
              <a 
                href={contactInfo.youtubeChannel}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300 w-full sm:w-auto"
                >
                  <Youtube className="w-5 h-5 mr-2" />
                  Visit Our Channel
                </Button>
              </a>
            </div>

            {/* Try AI Assistant */}
            <a
              href={contactInfo.customGPT}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sage-green hover:text-forest-green font-semibold transition-colors duration-300 group"
            >
              <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
              <span>Try our AI Assistant - Aajjicha Batwa</span>
              <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap gap-8">
              <div className="text-forest-green">
                <div className="text-4xl font-bold font-serif">20+</div>
                <div className="text-sage-green text-sm font-medium">Years Experience</div>
              </div>
              <div className="text-forest-green">
                <div className="text-4xl font-bold font-serif">60+</div>
                <div className="text-sage-green text-sm font-medium">TV Appearances</div>
              </div>
              <div className="text-forest-green">
                <div className="text-4xl font-bold font-serif">5000+</div>
                <div className="text-sage-green text-sm font-medium">Happy Patients</div>
              </div>
            </div>
          </div>

          {/* Right Image Section */}
          <div className="order-1 lg:order-2">
            <div className="relative">
              {/* Main Image with traditional frame effect */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                <img
                  src="https://images.pexels.com/photos/7615621/pexels-photo-7615621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Traditional Ayurvedic Herbs and Medicines"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-green/40 via-transparent to-transparent"></div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-sage-green rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-earth rounded-full opacity-20 blur-2xl"></div>

              {/* Floating Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-sage-green/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-serif font-bold text-forest-green text-lg">Traditional Panchakarma</p>
                    <p className="text-sm text-sage-green">Authentic 5-Step Detoxification</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave Decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};
